/* Toast after a quick add from a product card (snippets/toast.liquid): "Added · $6 more for free shipping · View bag".
   Card adds keep the bag closed (assets/quick-add.js asks LMB.cart.add for open: false, source: 'card'), so this says
   what happened and how far the bag is from free shipping, using the bag's totals that came back with the add
   ('lmb:cart:added' from assets/cart.js: no extra request). Errors from card adds show here too (they are read out by
   the card itself). Product-page adds and adds inside the bag don't use it, and it never shows over an open overlay
   (the bag, the menu; the size sheet shows its own errors). It stays about 3.2 s, paused while pointed at or
   focused, Esc dismisses it, and it never changes the URL. */
(() => {
  const LMB = (window.LMB = window.LMB || {});
  if (LMB.toast) return;
  const root = document.querySelector('[data-toast]');
  if (!root) return;

  const panel = root.querySelector('[data-toast-panel]');
  const text = root.querySelector('[data-toast-text]');
  const status = root.querySelector('[data-toast-status]');
  const view = root.querySelector('[data-toast-view]');
  const t = (key, fallback) => (LMB.strings && LMB.strings[key]) || fallback;
  const money = (cents) => (LMB.formatMoney ? LMB.formatMoney(cents) : `$${Math.round(cents) / 100}`);

  const DURATION = 3200;
  let timer = null;
  let hideTimer = null;
  let endsAt = 0;
  let left = DURATION;
  let hovered = false;
  let focused = false;
  // The card's "+" that made the add: where focus goes back when the bag opened from "View bag" closes.
  let returnTo = null;

  const isShown = () => root.classList.contains('is-shown');

  function schedule(ms) {
    clearTimeout(timer);
    endsAt = Date.now() + ms;
    timer = setTimeout(hide, ms);
  }

  /* Emptied first, so the same words twice in a row are read out twice. */
  function say(message) {
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  /* parts: the visible pieces, joined with " · "; announcement: what screen readers hear (none for errors). */
  function show({ parts, announcement = '', error = false, trigger = null }) {
    clearTimeout(hideTimer);
    text.replaceChildren();
    parts.forEach((part, index) => {
      if (index > 0) {
        const dot = document.createElement('span');
        dot.setAttribute('aria-hidden', 'true');
        dot.textContent = ' · ';
        text.append(dot);
      }
      text.append(document.createTextNode(part));
    });
    root.classList.toggle('is-error', error);
    view.hidden = error;
    returnTo = trigger;
    panel.hidden = false;
    // Force a reflow so the slide-up transition runs.
    void panel.offsetWidth;
    root.classList.add('is-shown');
    if (announcement) say(announcement);
    left = DURATION;
    if (hovered || focused) clearTimeout(timer);
    else schedule(DURATION);
  }

  function hide() {
    clearTimeout(timer);
    if (!isShown()) return;
    root.classList.remove('is-shown');
    const finish = () => {
      if (!isShown()) panel.hidden = true;
    };
    const duration = parseFloat(getComputedStyle(panel).transitionDuration) || 0;
    clearTimeout(hideTimer);
    hideTimer = setTimeout(finish, duration * 1000 + 50);
  }

  /* Paused while the shopper points at or focuses it (WCAG 2.2.1), then resumes with what was left (at least 1.5 s). */
  const pause = () => {
    if (!isShown()) return;
    clearTimeout(timer);
    left = Math.max(0, endsAt - Date.now());
  };
  const resume = () => {
    if (!isShown() || hovered || focused) return;
    schedule(Math.max(left, 1500));
  };
  panel.addEventListener('pointerenter', () => {
    hovered = true;
    pause();
  });
  panel.addEventListener('pointerleave', () => {
    hovered = false;
    resume();
  });
  panel.addEventListener('focusin', () => {
    focused = true;
    pause();
  });
  panel.addEventListener('focusout', (event) => {
    if (panel.contains(event.relatedTarget)) return;
    focused = false;
    resume();
  });
  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    const target = returnTo && returnTo.isConnected ? returnTo : null;
    focused = false;
    hide();
    if (target) target.focus({ preventScroll: true });
  });

  const openOverlay = () => document.querySelector('lmb-overlay.is-open');

  document.addEventListener('lmb:cart:added', (event) => {
    const detail = event.detail || {};
    if (detail.source !== 'card') return;
    // An overlay is open: the toast would sit over its buttons. The size sheet that made this add is still open
    // here (assets/quick-add.js closes it right after), so that one doesn't count. Any other (the bag, the menu, or
    // a sheet opened for another piece while this add was on its way) keeps the toast away: the bag shows the new
    // line and reads it out itself (assets/cart.js); anywhere else only the toast's status line speaks.
    const covering = openOverlay();
    const trigger = detail.trigger || null;
    const ownSheet =
      covering &&
      covering.id === 'SizeSheet' &&
      trigger &&
      (covering.contains(trigger) || (LMB.sizeSheet && trigger === LMB.sizeSheet.trigger()));
    const quiet = covering && !ownSheet;
    if (quiet && covering.id === 'CartDrawer') return;
    const parts = [t('toast_added', 'Added')];
    const threshold = Number(detail.threshold) || 0;
    const remaining = Number(detail.remaining);
    if (threshold > 0 && detail.remaining !== null && Number.isFinite(remaining)) {
      parts.push(
        remaining > 0
          ? t('toast_more', '[amount] more for free shipping').replace('[amount]', money(remaining))
          : t('toast_unlocked', 'Free shipping unlocked')
      );
    }
    const title = detail.item && detail.item.product_title;
    const first = title ? t('cart_added', '[title] added to bag').replace('[title]', title) : t('quick_add_added', 'Added to bag');
    const announcement = [first, ...parts.slice(1)].join('. ');
    if (quiet) {
      say(announcement);
      return;
    }
    show({ parts, announcement, trigger });
  });

  /* Not over an open overlay: in the size sheet the error is shown and read out by the sheet's own status line, and
     the toast would cover its "Add to bag" on phones, so a retry would land on the toast. */
  document.addEventListener('lmb:cart:error', (event) => {
    const detail = event.detail || {};
    if (detail.source !== 'card' || !detail.message || openOverlay()) return;
    show({ parts: [detail.message], error: true });
  });

  /* Any overlay opening (the bag, the size sheet, the menu) takes the toast's place. When the bag opened from
     "View bag", focus goes back to the card's "+" on close, since the toast will be gone by then. */
  document.addEventListener('lmb:overlay-open', () => hide());
  document.addEventListener('lmb:open', (event) => {
    const overlay = event.target;
    if (overlay && overlay.trigger === view && returnTo && returnTo.isConnected) overlay.trigger = returnTo;
  });

  LMB.toast = { show, hide };
})();
